import { CommonModule, DOCUMENT } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, Input, inject } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import { tagReferralLink } from '@aceable/shared-ui';
import type { SiteWindow } from '@core-mkt/interfaces/site-window';
import { BrandConfigurationService } from '@core-mkt/services/configuration/brand-configuration.service';
import { MarketingSyncUserService } from '@core-mkt/services/sync-user/marketing-sync-user.service';
import {
  TargetedSyncService,
  normalizePageBrandId,
  linkSignatureMatchesPage,
} from '@core-mkt/services/targeted-sync/targeted-sync.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCopy as faCopyIcon, faShare } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { ButtonProps } from './ambassador-invite';
import { AmbassadorInvite } from './ambassador-invite';

@Component({
  selector: 'ambassador-invite',
  templateUrl: './ambassador-invite.component.html',
  standalone: true,
  imports: [CommonModule, FaIconComponent],
})
export class AmbassadorInviteComponent implements OnInit, OnDestroy {
  @Input() data: AmbassadorInvite;
  theme: ThemeInterface;

  private readonly destroy$ = new Subject<void>();

  // Logged-in ambassador state
  isLoggedIn = false;
  loggedInAmbassadorUrl = '';
  urlCopied = false;
  faCopyUrl = faCopyIcon;
  faShareLink = faShare;

  private readonly themeParser = inject(ThemeParserService);
  private readonly redactor = inject(WysiwygRedactorService);
  private readonly syncUserService = inject(MarketingSyncUserService);
  private readonly brandConfig = inject(BrandConfigurationService);
  private readonly targetedSync = inject(TargetedSyncService);
  private readonly document = inject(DOCUMENT);

  get buttonProps(): ButtonProps {
    return {
      style: this.data.ctaStyle,
      label: this.data.ctaLabel || 'GET MY REFERRAL LINK',
    };
  }

  get ambassadorInviteTitle(): SafeHtml {
    return this.data.ambassadorInviteTitle;
  }

  get ambassadorInviteContent(): SafeHtml {
    return this.data.ambassadorInviteContent;
  }

  get ambassadorInviteRightContent(): SafeHtml {
    return this.data.ambassadorInviteRightContent;
  }

  ngOnInit(): void {
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.data.backgroundColor,
      backgroundImage: this.data.backgroundImage?.[0],
      textColor: '',
      columnCount: 0,
      columnDistribution: '',
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
    this.data.ambassadorInviteTitle = this.redactor.bypassSanitizer(this.data.ambassadorInviteTitle);
    this.data.ambassadorInviteContent = this.redactor.bypassSanitizer(this.data.ambassadorInviteContent);
    this.data.ambassadorInviteRightContent = this.redactor.bypassSanitizer(
      this.data.ambassadorInviteRightContent,
      'lazy',
    );

    // Subscribe on EVERY brand. This used to sit behind `ambassadorIntegrationId`, which meant the
    // logged-in link could never render on aa/dec/ids/aarp — they have no such id — so an
    // AceableAgent purchaser saw no link on the AceableAgent invite page at all. That id exists only
    // to drive the legacy MBSY embed (see `BrandConfig.ambassadorIntegrationId`), and the sibling
    // RAF container already documents that using it as a brand gate breaks exactly those brands.
    // It now guards only the MBSY-specific work below; the link itself is governed by the
    // brand + signature gate, which fails closed on its own.
    this.syncUserService.syncUserReady$.pipe(takeUntil(this.destroy$)).subscribe((response) => {
      // `aCustEmail` feeds the legacy MBSY embed, which only ace/ins/hsp ship. Setting it on a brand
      // with no embed would be new behaviour, so this half stays gated.
      if (this.brandConfig.config?.ambassadorIntegrationId) {
        const win = this.document.defaultView as unknown as SiteWindow | null;
        if (win && response?.success && response.data?.email) {
          win.aCustEmail = response.data.email;
        } else if (win) {
          win.aCustEmail = undefined;
        }
      }

      // Only ever render the logged-in referral link when we actually have one. Whether we show
      // it is then gated on the account's brand matching this page's brand (see verifyBrand...).
      if (response?.success && response.data?.ambassadorUrl) {
        this.verifyBrandAndSetLoggedInLink(response.data.ambassadorUrl);
      } else {
        // No usable url on this emission -> nothing may remain rendered from a previous one.
        this.isLoggedIn = false;
        this.loggedInAmbassadorUrl = '';
      }
    });
  }

  /**
   * Fail-CLOSED brand + vertical gate for the logged-in Ambassador link (EPS-4664).
   *
   * `sync-marketing` returns an `ambassadorUrl` regardless of which brand owns the account, so on
   * its own it leaks a user's link onto a sibling brand's invite page. Two checks are needed, and
   * NEITHER is sufficient alone:
   *
   * - BRAND — `targeted-sync.brandId` vs this page's `iterable.brandId`. Separates ACE/IDS/DEC/AARP.
   * - VERTICAL — the link's OWN host + `utm_campaign` tokens vs this page's signature. Separates the
   *   verticals *within* ACE, which all report `brandId: ACE` (Aceable, AceableAgent, INS, MTG, HSP).
   *
   * Brand alone renders an AceableAgent link on Aceable; vertical alone renders an I Drive Safely
   * link on Aceable (both are driving). Anything else — no page brand, an unrecognised link, request
   * error, missing `brandId`, or either mismatch — leaves `isLoggedIn` false so the intake box shows.
   */
  private verifyBrandAndSetLoggedInLink(ambassadorUrl: string): void {
    const pageBrandId = normalizePageBrandId(this.brandConfig.config?.iterable?.brandId);
    // No page brand to compare against → fail closed (intake box).
    if (!pageBrandId) {
      return;
    }

    // Vertical half of the gate: does this link identify itself as belonging on THIS page? Derived
    // from the URL rather than a separate API field, so it needs no backend change to be correct.
    if (!linkSignatureMatchesPage(ambassadorUrl, this.brandConfig.config?.iterable)) {
      return;
    }

    this.targetedSync
      .getTargetedSyncUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (targeted) => {
          const accountBrandId = (targeted?.brandId ?? '').toUpperCase();
          if (accountBrandId && accountBrandId === pageBrandId) {
            this.isLoggedIn = true;
            // Same invite-page campaign the site-wide RAF modal applies, so both show one link. Set
            // after the signature gate above, which reads GetAmbassador's original campaign.
            this.loggedInAmbassadorUrl = tagReferralLink(ambassadorUrl, this.brandConfig.id, 'Invite');
          } else {
            // Clear explicitly rather than relying on these still holding their initial values.
            // `syncUserReady$` is `take(1)` today (marketing-sync-user.service.ts), so this method
            // runs at most once per component instance and the reset is defence in depth -- it keeps
            // the gate fail-closed if that ever changes, without depending on it having changed.
            this.isLoggedIn = false;
            this.loggedInAmbassadorUrl = '';
          }
        },
        // Fail closed on any error so the intake box shows even if the service's own
        // error handling is ever removed — never leave the link rendered on error.
        error: () => {
          this.isLoggedIn = false;
          this.loggedInAmbassadorUrl = '';
        },
      });
  }

  copyAmbassadorUrl(): void {
    const nav = this.document.defaultView?.navigator;
    if (!nav?.clipboard?.writeText) {
      return;
    }

    void nav.clipboard
      .writeText(this.loggedInAmbassadorUrl)
      .then(() => {
        this.urlCopied = true;
        setTimeout(() => {
          this.urlCopied = false;
        }, 2000);
      })
      .catch(() => {
        this.urlCopied = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
